import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICalSync = () => {
  const navigate = useNavigate();
  const [syncStatus, setSyncStatus] = useState([]);
  const [recentMissions, setRecentMissions] = useState([]);
  const [pendingDuplicates, setPendingDuplicates] = useState([]);
  const [oldCheckoutsCount, setOldCheckoutsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncDate, setLastSyncDate] = useState(null);
  const [lastSyncReport, setLastSyncReport] = useState(null);
  
  // Configuration du scheduler
  const [schedulerConfig, setSchedulerConfig] = useState({
    interval_minutes: 5,
    enabled: true
  });
  const [savingConfig, setSavingConfig] = useState(false);

  const [ignoredMissions, setIgnoredMissions] = useState([]);

  useEffect(() => {
    loadData();
    loadSchedulerConfig();
    loadIgnored();
    const interval = setInterval(loadData, 120000);
    return () => clearInterval(interval);
  }, []);

  const loadIgnored = async () => {
    try {
      const res = await axios.get(`${API}/ical-ignored`);
      setIgnoredMissions(res.data);
    } catch (e) {
      console.error("Erreur chargement ignored:", e);
    }
  };

  const handleClearIgnored = async (housingId, housingPseudo) => {
    try {
      await axios.delete(`${API}/ical-ignored/housing/${housingId}`);
      toast.success(`Blocages supprimés pour ${housingPseudo}`);
      await loadIgnored();
      // Relancer la synchro pour ce logement
      await handleSyncOne(housingId);
    } catch (e) {
      toast.error("Erreur: " + (e.response?.data?.detail || e.message));
    }
  };

  const loadData = async () => {
    try {
      const [statusResponse, missionsResponse, duplicatesResponse] = await Promise.all([
        axios.get(`${API}/ical/sync-status`),
        axios.get(`${API}/ical/recent-missions?limit=50`),
        axios.get(`${API}/ical/duplicates`)
      ]);

      // Trier les missions par date (plus proche en premier)
      const sortedMissions = (missionsResponse.data.missions || []).sort((a, b) => {
        const dateA = new Date(a.date_mission);
        const dateB = new Date(b.date_mission);
        return dateA - dateB; // Ordre chronologique
      });

      // Filtrer les doublons : ne garder que ceux qui ne sont pas encore créés par iCal
      const filteredDuplicates = (duplicatesResponse.data.duplicates || []).filter(dup => 
        dup.status === 'pending' && dup.existing_source !== 'ical_airbnb' && dup.existing_source !== 'ical_booking'
      );

      setSyncStatus(statusResponse.data.housings);
      setRecentMissions(sortedMissions);
      setPendingDuplicates(filteredDuplicates);
      setOldCheckoutsCount(missionsResponse.data.old_checkouts_count || 0);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      setLoading(false);
    }
  };

  const loadSchedulerConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/ical-scheduler/config`);
      setSchedulerConfig(response.data);
    } catch (error) {
      console.error("Erreur chargement config scheduler:", error);
    }
  };

  const handleSaveSchedulerConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.post(`${API}/admin/ical-scheduler/configure?interval_minutes=${schedulerConfig.interval_minutes}&enabled=${schedulerConfig.enabled}`);
      toast.success(`Configuration sauvegardée !\n\nSynchronisation ${schedulerConfig.enabled ? 'activée' : 'désactivée'}\nIntervalle: ${schedulerConfig.interval_minutes} minutes`);
    } catch (error) {
      console.error("Erreur sauvegarde config:", error);
      toast.error("Erreur lors de la sauvegarde de la configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSyncAll = async () => {
    // Action directe sans confirmation

    setSyncing(true);
    try {
      const response = await axios.post(`${API}/ical/sync-all`);
      setLastSyncDate(new Date());
      setLastSyncReport(response.data);
      
      // Recharger les données
      await loadData();
      
      // Message de succès
      toast.success(`Synchronisation : ${response.data.missions_created} mission(s) créée(s), ${response.data.duplicates_detected} doublon(s)`);
    } catch (error) {
      console.error("Erreur synchro:", error);
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncOne = async (housingId) => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/ical/sync-housing/${housingId}`);
      const report = response.data.report;
      
      await loadData();
      
      toast.success(`Synchro terminée : ${report.missions_created} mission(s) créée(s)`);
    } catch (error) {
      console.error("Erreur synchro:", error);
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  const handleValidateDuplicate = async (duplicateId) => {
    // Action directe sans confirmation

    try {
      await axios.post(`${API}/ical/duplicates/${duplicateId}/validate`);
      toast.success("Mission créée avec succès");
      await loadData();
    } catch (error) {
      console.error("Erreur validation:", error);
      toast.error("Erreur lors de la validation");
    }
  };

  const handleRejectDuplicate = async (duplicateId) => {
    // Action directe sans confirmation

    try {
      await axios.post(`${API}/ical/duplicates/${duplicateId}/reject`);
      toast.success("Doublon ignoré");
      await loadData();
    } catch (error) {
      console.error("Erreur rejet:", error);
      toast.error("Erreur lors du rejet");
    }
  };

  const goToHousingEdit = (housingId) => {
    navigate(`/housings?edit=${housingId}`);
  };

  const getSourceBadge = (source) => {
    if (source === 'ical_airbnb') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-red-100 text-red-700">
          Airbnb
        </span>
      );
    }
    if (source === 'ical_booking') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-blue-100 text-blue-700">
          Booking
        </span>
      );
    }
    if (source === 'ical_superhote') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-purple-100 text-purple-700">
          Superhôte
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
        ✋ Manuel
      </span>
    );
  };

  const getStatusIcon = (status) => {
    if (!status) return "⚪";
    if (status.errors && status.errors.length > 0) return "🔴";
    if (status.ical_airbnb && status.ical_booking) return "🟢";
    if (status.ical_airbnb || status.ical_booking) return "🟡";
    return "⚪";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Jamais";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    return `Il y a ${days}j`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl">🔄 Chargement...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">📅 Synchronisation iCal</h1>
        <button
          onClick={handleSyncAll}
          disabled={syncing}
          className={`btn-primary ${syncing ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {syncing ? "🔄 Synchronisation..." : "🔄 Synchroniser maintenant"}
        </button>
      </div>

      {/* Configuration du Scheduler */}
      <div className="card mb-6 border-2 border-blue-300">
        <div className="card-header bg-gradient-to-r from-blue-50 to-indigo-50">
          <h2 className="text-xl font-semibold text-blue-900">
            ⚙️ Configuration de la synchronisation automatique
          </h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            {/* Intervalle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🕐 Synchroniser toutes les
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="1440"
                  value={schedulerConfig.interval_minutes}
                  onChange={(e) => setSchedulerConfig(prev => ({
                    ...prev,
                    interval_minutes: parseInt(e.target.value) || 5
                  }))}
                  className="form-input w-24"
                />
                <span className="text-gray-600">minutes</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Entre 1 et 1440 minutes (24h)
              </p>
            </div>

            {/* Activation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔌 État
              </label>
              <button
                onClick={() => setSchedulerConfig(prev => ({
                  ...prev,
                  enabled: !prev.enabled
                }))}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  schedulerConfig.enabled
                    ? 'bg-green-100 text-green-700 border-2 border-green-400 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-600 border-2 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {schedulerConfig.enabled ? '✅ Activé' : '⏸️ Désactivé'}
              </button>
            </div>

            {/* Bouton Sauvegarder */}
            <div>
              <button
                onClick={handleSaveSchedulerConfig}
                disabled={savingConfig}
                className={`btn-primary w-full ${savingConfig ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {savingConfig ? '💾 Sauvegarde...' : '💾 Sauvegarder'}
              </button>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              ℹ️ La synchronisation automatique récupère les check-outs depuis Airbnb, Booking et Superhôte et crée automatiquement les missions de ménage.
            </p>
          </div>
        </div>
      </div>

      {lastSyncDate && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-800">
            ✅ Dernière synchronisation : {lastSyncDate.toLocaleString('fr-FR')}
          </p>
          {lastSyncReport && (
            <div className="text-sm text-green-700 mt-2">
              {lastSyncReport.missions_created} mission(s) créée(s) • {lastSyncReport.duplicates_detected} doublon(s) détecté(s)
            </div>
          )}
        </div>
      )}

      {/* Section Doublons en attente */}
      {pendingDuplicates.length > 0 && (
        <div className="card mb-6 border-2 border-orange-300">
          <div className="card-header bg-gradient-to-r from-orange-50 to-yellow-50">
            <h2 className="text-xl font-semibold text-orange-900">
              ⚠️ Doublons détectés ({pendingDuplicates.length})
            </h2>
            <p className="text-sm text-orange-700 mt-1">
              Ces missions existent déjà dans le planning. Validez pour créer malgré tout, ou supprimez pour ignorer.
            </p>
          </div>
          <div className="card-body overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Client / Logement
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date Check-out
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Source iCal
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Mission existante
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingDuplicates.map((dup) => (
                  <tr key={dup.id} className="hover:bg-orange-50">
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">{dup.client_name}</div>
                      <button
                        onClick={() => goToHousingEdit(dup.housing_id)}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        🏠 {dup.housing_pseudo}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium">
                        {new Date(dup.date_mission).toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {getSourceBadge(dup.ical_source)}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="text-gray-600">
                        {getSourceBadge(dup.existing_source)}
                      </div>
                      {dup.existing_mission_notes && (
                        <div className="text-xs text-gray-500 mt-1 italic truncate max-w-xs">
                          {dup.existing_mission_notes.substring(0, 50)}...
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleValidateDuplicate(dup.id)}
                          className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm font-medium"
                          title="Valider et créer la mission"
                        >
                          ✅ Valider
                        </button>
                        <button
                          onClick={() => handleRejectDuplicate(dup.id)}
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm font-medium"
                          title="Supprimer ce doublon"
                        >
                          ❌ Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Section Missions iCal ignorées */}
      {ignoredMissions.length > 0 && (
        <div className="card mb-6 border-2 border-red-300">
          <div className="card-header bg-gradient-to-r from-red-50 to-pink-50">
            <h2 className="text-xl font-semibold text-red-900">
              Missions bloquees ({ignoredMissions.length})
            </h2>
            <p className="text-sm text-red-700 mt-1">
              Ces missions iCal ont ete supprimees/deplacees et ne seront pas recreees. Debloquez pour forcer la synchro.
            </p>
          </div>
          <div className="card-body">
            {(() => {
              const grouped = {};
              ignoredMissions.forEach(m => {
                const key = m.housing_id;
                if (!grouped[key]) grouped[key] = { housing_id: key, housing_pseudo: m.housing_pseudo || key, dates: [] };
                grouped[key].dates.push(m.original_date);
              });
              return Object.values(grouped).map(group => (
                <div key={group.housing_id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div>
                    <span className="font-medium">{group.housing_pseudo}</span>
                    <span className="text-sm text-gray-500 ml-2">
                      ({group.dates.length} date(s) bloquee(s) : {group.dates.sort().join(', ')})
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/ical-ignored/housing/${group.housing_id}`);
                          toast.success(`Blocages supprimés pour ${group.housing_pseudo}`);
                          await loadIgnored();
                        } catch (e) {
                          toast.error("Erreur: " + (e.response?.data?.detail || e.message));
                        }
                      }}
                      className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                      data-testid={`delete-ignored-${group.housing_id}`}
                    >
                      Supprimer
                    </button>
                    <button
                      onClick={() => handleClearIgnored(group.housing_id, group.housing_pseudo)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      data-testid={`clear-ignored-${group.housing_id}`}
                    >
                      Debloquer + Synchro
                    </button>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Tableau de contrôle principal */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="text-xl font-semibold">📊 Vue d'ensemble des logements</h2>
          <p className="text-sm text-gray-600 mt-1">
            {syncStatus.length} logement(s) avec iCal configuré
          </p>
        </div>
        <div className="card-body overflow-x-auto">
          {syncStatus.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📅</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun logement configuré
              </h3>
              <p className="text-gray-500">
                Configurez des liens iCal dans la fiche de vos logements pour activer la synchronisation automatique.
              </p>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Logement
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Client
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    iCal Airbnb
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    iCal Booking
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Dernière synchro
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {syncStatus.map((status) => (
                  <tr key={status.housing_id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-center text-2xl">
                      {getStatusIcon(status)}
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => goToHousingEdit(status.housing_id)}
                        className="font-medium text-blue-600 hover:text-blue-800 underline"
                        title="Ouvrir la fiche logement"
                      >
                        {status.housing_pseudo}
                      </button>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {status.client_name}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {status.ical_airbnb ? (
                        <span className="text-green-600 font-bold">✅</span>
                      ) : (
                        <span className="text-gray-400">❌</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {status.ical_booking ? (
                        <span className="text-green-600 font-bold">✅</span>
                      ) : (
                        <span className="text-gray-400">❌</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {formatDate(status.last_sync)}
                      {status.missions_created_last > 0 && (
                        <div className="text-xs text-green-600 mt-1">
                          +{status.missions_created_last} mission(s)
                        </div>
                      )}
                      {status.duplicates_last > 0 && (
                        <div className="text-xs text-orange-600 mt-1">
                          ⚠️ {status.duplicates_last} doublon(s)
                        </div>
                      )}
                      {status.errors && status.errors.length > 0 && (
                        <div className="text-xs text-red-600 mt-1">
                          ❌ {status.errors.length} erreur(s)
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button
                        onClick={() => handleSyncOne(status.housing_id)}
                        disabled={syncing}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        title="Synchroniser maintenant"
                      >
                        🔄 Synchro
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Tableau des missions récentes - Groupées par client */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-xl font-semibold">🌀 Missions créées par iCal (Check-outs uniquement)</h2>
          <p className="text-sm text-gray-600 mt-1">
            Classées par ordre chronologique, regroupées par client (à partir de la veille)
          </p>
        </div>
        
        {/* Résumé des anciens check-outs archivés */}
        {oldCheckoutsCount > 0 && (
          <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
            <span className="text-gray-600 text-sm">
              📦 <strong>{oldCheckoutsCount}</strong> ancien{oldCheckoutsCount > 1 ? 's' : ''} check-out{oldCheckoutsCount > 1 ? 's' : ''} archivé{oldCheckoutsCount > 1 ? 's' : ''} (avant hier)
            </span>
          </div>
        )}
        
        <div className="card-body overflow-x-auto">
          {recentMissions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucune mission synchronisée pour le moment
            </div>
          ) : (() => {
            // Grouper par client
            const groupedByClient = recentMissions.reduce((acc, mission) => {
              const clientName = mission.client_name || 'Client inconnu';
              if (!acc[clientName]) {
                acc[clientName] = [];
              }
              acc[clientName].push(mission);
              return acc;
            }, {});

            return (
              <div className="space-y-6">
                {Object.entries(groupedByClient).map(([clientName, missions]) => (
                  <div key={clientName} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* En-tête client */}
                    <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-900">
                        👤 {clientName} ({missions.length} check-out{missions.length > 1 ? 's' : ''})
                      </h3>
                    </div>
                    
                    {/* Missions du client */}
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date Check-out
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Logement
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Source
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Créé le
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {missions.map((mission) => (
                          <tr key={mission.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 font-medium text-blue-600">
                              {new Date(mission.date_mission).toLocaleDateString('fr-FR', {
                                weekday: 'short',
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </td>
                            <td className="px-4 py-4">{mission.housing_pseudo}</td>
                            <td className="px-4 py-4">
                              {getSourceBadge(mission.source)}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {formatDate(mission.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Aide */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">💡 Comment ça marche ?</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Configurez les liens iCal dans la fiche de chaque logement</li>
          <li>• <strong>Seuls les check-outs sont pris en compte</strong> (durée de séjour entre 2 et 30 jours)</li>
          <li>• Les missions sont créées automatiquement à la date du check-out</li>
          <li>• Si un doublon est détecté (mission manuelle existante), vous serez alerté</li>
          <li>• <strong>Les doublons iCal vs iCal ne sont PAS signalés</strong> (déjà gérés automatiquement)</li>
          <li>• La synchronisation se fait automatiquement toutes les 30 minutes</li>
          <li>• Vous pouvez aussi lancer une synchro manuelle à tout moment</li>
        </ul>
      </div>
    </div>
  );
};

export default ICalSync;
